import { useContext, useEffect, useState } from 'react';
import Context from "../../context/Context";
import { Stack, DialogContent, useMediaQuery, IconButton, Dialog, List, ListItemButton, Typography, Button, Box } from "@mui/material"
import CloseIcon from "@mui/icons-material/Close";
import SearchOffIcon from '@mui/icons-material/SearchOff';
import SearchInput from "../searchBar/SearchInput"
import NoResults from "../NoResults"
import BondStatusChip from "../BondStatusChip"
import { instrumentSearchSuggestions, getFeaturedInstruments } from "../../api/app/instrumentRequests";
import { bondNameFormatter } from "../../utils/formatters"
import { useNavigate } from 'react-router-dom';

const SearchSelectModal = ({ open, onClose, onSelect, displayHotLinks, displayNavigateToAdvancedSearch }) => {

    const navigate = useNavigate()

    const downMDScreenSize = useMediaQuery((theme) => theme.breakpoints.down('md'));

    const { showSnackbar } = useContext(Context);

    const [searchValue, setSearchValue] = useState("")
    const [highlightedResultId, setHighlightedResultId] = useState("")
    const [results, setResults] = useState([])

    const clearState = () => {
        setSearchValue("")
    }

    const handleSearchValueChange = (event) => {
        setSearchValue(event.target.value)
    }

    const handleChangeHighLightResultByHover = (event, resultId) => {
        setHighlightedResultId(resultId)
    }

    const handleOmniChartNavigation = (event, selectionId) => {
        if (event) { event.stopPropagation() }
        navigate("/omnichart", { state: { instrumentId: selectionId } })
    }

    const handleAdvancedSearchNavigation = () => {
        navigate(`/bonds?searchTerm=${searchValue}`)
    }

    const handleSelectResult = (event, selectionId) => {
        onSelect(selectionId)
        onClose()
    }

    useEffect(() => {
        if (searchValue.length) {
            instrumentSearchSuggestions(searchValue)
                .then((results) => {
                    setResults(results)
                })
                .catch(e => {
                    showSnackbar(e.message, 'warning');
                })
        } else {
            getFeaturedInstruments('price', 'upper').then((featuredResults) => {
                setResults(featuredResults)
            }).catch(e => {
                showSnackbar(e.message, 'warning');
            })
        }
    }, [searchValue])

    useEffect(() => {
        if (results.length) {
            setHighlightedResultId(results.at(0).isin)
        }
    }, [results])

    useEffect(() => {
        clearState()
    }, [open])

    return (
        <Dialog
            onClose={onClose}
            open={open}
            fullScreen={downMDScreenSize}
            maxWidth="md"
            fullWidth={true}
            scroll='paper'
            slotProps={
                {
                    backdrop: {
                        sx: {
                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                            backdropFilter: 'blur(12px) saturate(180%)',
                            WebkitBackdropFilter: 'blur(12px) saturate(180%)',
                            transition: 'all 0.4s ease',
                        }
                    },
                    paper: {
                        sx: {
                            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                            padding: 2,
                        }
                    }
                }
            }
        >
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <SearchInput
                    params={{ fullWidth: true, autoFocus: true, InputProps: { value: searchValue, onChange: handleSearchValueChange, placeholder: "Search here" } }}
                    highlightedResult={highlightedResultId}
                    onSubmitHandler={handleSelectResult}
                />
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{ color: "complementary.main", marginRight: 3 }}
                >
                    <CloseIcon />
                </IconButton>
            </Stack>
            <DialogContent dividers sx={{ px: 0 }}>
                {results.length ?
                    <List sx={{
                        maxHeight: { lg: 5 * 64 + 16 },
                        overflowY: "auto",
                    }}>
                        {
                            results.map((result) => (
                                <ListItemButton
                                    key={result.isin}
                                    onMouseEnter={(event) => handleChangeHighLightResultByHover(event, result.isin)}
                                    sx={{ display: 'flex', justifyContent: 'space-between', backgroundColor: result.isin === highlightedResultId ? "action.hover" : "white" }}
                                    onClick={(event) => handleSelectResult(event, result.isin)}
                                >
                                    <Stack flex={1} direction="row" alignItems="center" justifyContent="space-between">
                                        <Stack>
                                            <Typography color="primary.main">{bondNameFormatter(result)}</Typography>
                                            <Typography color="text.secondary">{result.isin}</Typography>
                                        </Stack>
                                        {(result.isin !== highlightedResultId || !displayHotLinks) && <BondStatusChip status={result.status} maturity={result.maturity} chipSize="small" />}
                                        {!downMDScreenSize && result.isin === highlightedResultId && displayHotLinks &&
                                            <Button variant="outlined" onClick={(event) => handleOmniChartNavigation(event, result.isin)}>Launch OmniChart</Button>
                                        }
                                    </Stack>
                                </ListItemButton>
                            ))
                        }
                    </List>
                    :
                    <NoResults Icon={SearchOffIcon} title="No results were found" description="Please try with a different search term." />
                }
            </DialogContent>
            {
                displayNavigateToAdvancedSearch &&
                <Box pr={4} pt={2} display="flex" justifyContent="flex-end">
                    <Button onClick={handleAdvancedSearchNavigation}>Open advanced search</Button>
                </Box>
            }
        </Dialog >
    )
}

export default SearchSelectModal; 